import { useState, useEffect } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/dashboard/StatusBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Search, Pencil, Trash2 } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";
import { canManageModule } from "@/lib/permissions";
import { ServerPagination } from "@/components/ui/server-pagination";

const StudentServices = () => {
  const { roles } = useAuth();
  const canManage = canManageModule(roles, "student_services");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState({ student_id: "", subject: "", description: "", category: "general", status: "open" });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setCurrentPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ["service-requests", debouncedSearch, currentPage, pageSize],
    queryFn: async () => {
      let q = supabase.from("service_requests").select("*, students(first_name, last_name, student_number)", { count: "exact" }).order("created_at", { ascending: false });
      const from = (currentPage - 1) * pageSize;
      q = q.range(from, from + pageSize - 1);
      const { data, error, count } = await q;
      if (error) throw error;
      let filtered = data ?? [];
      if (debouncedSearch) {
        const s = debouncedSearch.toLowerCase();
        filtered = filtered.filter((r: any) =>
          `${r.students?.first_name} ${r.students?.last_name} ${r.subject} ${r.category}`.toLowerCase().includes(s)
        );
      }
      return { requests: filtered, totalCount: count ?? 0 };
    },
  });

  const { data: students = [] } = useQuery({
    queryKey: ["students-select"],
    queryFn: async () => {
      const { data } = await supabase.from("students").select("id, first_name, last_name, student_number").order("first_name");
      return data ?? [];
    },
  });

  const requests = data?.requests ?? [];
  const totalCount = data?.totalCount ?? 0;

  const saveMutation = useMutation({
    mutationFn: async (d: any) => {
      const payload = { student_id: d.student_id, subject: d.subject, description: d.description, category: d.category, status: d.status };
      if (d.id) {
        const { error } = await supabase.from("service_requests").update(payload).eq("id", d.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("service_requests").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["service-requests"] });
      toast({ title: editing ? "Request updated" : "Request created" });
      setFormOpen(false);
      setEditing(null);
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase.from("service_requests").update({ status }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["service-requests"] });
      toast({ title: "Status updated" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("service_requests").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["service-requests"] });
      toast({ title: "Request deleted" });
    },
  });

  const openNew = () => { setEditing(null); setForm({ student_id: "", subject: "", description: "", category: "general", status: "open" }); setFormOpen(true); };
  const openEdit = (r: any) => { setEditing(r); setForm({ student_id: r.student_id, subject: r.subject, description: r.description, category: r.category || "general", status: r.status }); setFormOpen(true); };

  return (
    <AppLayout>
      <div className="space-y-3 sm:space-y-4">
        <div className="page-header">
          <div>
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold">Student Services</h1>
            <p className="text-muted-foreground text-sm">Manage student requests, complaints, and support</p>
          </div>
          {canManage && <Button onClick={openNew}><Plus className="mr-2 h-4 w-4" />New Request</Button>}
        </div>

        <Dialog open={formOpen} onOpenChange={setFormOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Edit Request" : "New Request"}</DialogTitle></DialogHeader>
            <form onSubmit={e => { e.preventDefault(); saveMutation.mutate({ ...form, id: editing?.id }); }} className="space-y-4">
              <div><Label>Student *</Label>
                <Select value={form.student_id} onValueChange={v => setForm(f => ({ ...f, student_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select student" /></SelectTrigger>
                  <SelectContent>{students.map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} ({s.student_number})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Subject *</Label><Input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} required /></div>
              <div><Label>Description *</Label><Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} required /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Category</Label>
                  <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="academic">Academic</SelectItem>
                      <SelectItem value="financial">Financial</SelectItem>
                      <SelectItem value="welfare">Welfare</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Status</Label>
                  <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="escalated">Escalated</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button type="submit" disabled={saveMutation.isPending} className="w-full">{saveMutation.isPending ? "Saving..." : "Save"}</Button>
            </form>
          </DialogContent>
        </Dialog>

        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search requests..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Service Requests</CardTitle>
            <CardDescription>{totalCount} requests</CardDescription>
          </CardHeader>
          <CardContent className="table-responsive">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Student</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.map((r: any) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.students?.first_name} {r.students?.last_name}</TableCell>
                    <TableCell>{r.subject}</TableCell>
                    <TableCell>{r.category}</TableCell>
                    <TableCell><StatusBadge status={r.status} /></TableCell>
                    <TableCell>{new Date(r.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="flex gap-1">
                      {canManage && r.status === "open" && <Button variant="outline" size="sm" onClick={() => statusMutation.mutate({ id: r.id, status: "in_progress" })}>Start</Button>}
                      {canManage && r.status === "in_progress" && <Button variant="outline" size="sm" onClick={() => statusMutation.mutate({ id: r.id, status: "resolved" })}>Resolve</Button>}
                      {canManage && <Button variant="ghost" size="sm" onClick={() => openEdit(r)}><Pencil className="h-3 w-3" /></Button>}
                      {canManage && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild><Button variant="ghost" size="sm"><Trash2 className="h-3 w-3 text-destructive" /></Button></AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>Delete request?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => deleteMutation.mutate(r.id)}>Delete</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {requests.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">{isLoading ? "Loading..." : "No requests found"}</TableCell></TableRow>}
              </TableBody>
            </Table>
            <ServerPagination currentPage={currentPage} pageSize={pageSize} totalCount={totalCount} onPageChange={setCurrentPage} onPageSizeChange={setPageSize} />
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};

export default StudentServices;
